import { useEffect, useRef, useState } from 'react'
import { EmojiClickData } from 'emoji-picker-react'
import _isEmpty from 'lodash/isEmpty'

import {
  useEditMessageMutation,
  useSendMessageMutation,
  useSendThreadMessageMutation,
  useSendVanishMessageMutation,
} from 'app/services/conversation.service'
import uploadApi from 'app/axiosApi'
import { otrActions } from 'app/slices/otr.slice'
import { ICustomFile, IFiles } from 'types/file.types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { conversationActions } from 'app/slices/conversations.slice'
import {
  getInputValueByfullname,
  geValueByfullname,
  parseUserDataFromInputData,
} from 'utils/helpers/messageHelper'
import { IMessageContent, MessageStatus } from 'types/conversation.types'
import { FixMeLater } from 'types'
import { v4 } from 'uuid'
import { IUser, IUserData } from 'types/user.types'
import { values, cloneDeep } from 'lodash'
import { uploadActions } from 'app/slices/upload.slice'

const initialState = { text: '', value: '', mentionedUsers: [], files: [], users: {} }
export interface IMessageInputState {
  text: string
  value: string
  mentionedUsers: string[]
  files: IFiles
  users: IUserData
}

const isEmpty = (str: string) => !str?.trim()?.length

const inputDataValidation = (text: string, files: IFiles) => {
  return !isEmpty(text) || files.length > 0
}

export const getParseMentionedUsers = (text: string) => {
  // Gerçek mention formatını (@[DisplayName]&_id:userId& pattern) kontrol et
  const mentionRegex = /@\[([^\]]*)\]&_id:([^&]+)&/g
  const mentionedUsers: string[] = []
  let match
  while ((match = mentionRegex.exec(text)) !== null) {
    const userId = match[2]
    if (userId && !mentionedUsers.includes(userId)) {
      mentionedUsers.push(userId)
    }
  }
  return mentionedUsers
}

const useMessageInput = ({
  roomId,
  messageIdInThread,
  messageId,
  isOtr,
  isEditMessage = false,
  initialInputData,
  pendingHandler,
  users,
}: {
  roomId: string
  messageIdInThread?: string
  messageId?: string
  isOtr?: boolean
  isEditMessage?: boolean
  initialInputData?: IMessageContent
  pendingHandler?: (message: FixMeLater) => void
  users?: IUserData
}) => {
  const replicateInputData = useRef<IMessageInputState>({ ...initialState })
  const [isValid, setValid] = useState(false)
  const [inputData, setInputData] = useState<IMessageInputState>({
    ...initialState,
    ...initialInputData,
  })
  const [editMessageData, setEditMessageData] = useState<IMessageInputState>({
    ...initialState,
    ...initialInputData,
  })
  const isEditMode = isEditMessage && messageId

  // const mounted = useIsMounted()
  const dispatch = useAppDispatch()
  const myId = useAppSelector((state) => state.auth.id)
  const { draftData } = useAppSelector((state) => state.message)
  const [sendMessage, { data, isSuccess }] = useSendMessageMutation()
  const [sendThreadMessage] = useSendThreadMessageMutation()
  const [sendOtrMessage] = useSendVanishMessageMutation()
  const [sendEditedMessage] = useEditMessageMutation()

  useEffect(() => {
    if (data && isSuccess) {
      dispatch(conversationActions.updateMessageByRoomId(data.data))
    }
  }, [data, isSuccess])

  useEffect(() => {
    replicateInputData.current = isEditMode ? editMessageData : inputData
  }, [inputData, editMessageData, isEditMode])

  useEffect(() => {
    window.onbeforeunload = function () {
      addDataInDraft(replicateInputData.current, true, messageIdInThread, roomId)
      if (inputDataValidation(replicateInputData.current.text, replicateInputData.current.files)) {
        return true
      }
    }

    return () => {
      addDataInDraft(replicateInputData.current, false, messageIdInThread, roomId)
      window.onbeforeunload = null
    }
  }, [messageIdInThread, roomId])

  useEffect(() => {
    if (isEditMode) setValid(inputDataValidation(editMessageData.text, editMessageData.files))
    setValid(inputDataValidation(inputData.text, inputData.files))
  }, [inputData.text, inputData.files, editMessageData.text, editMessageData.files, isEditMode])

  useEffect(() => {
    if (isEditMode && initialInputData) {
      setEditMessageData({ ...initialState, ...initialInputData })
    }
  }, [isEditMode, initialInputData])

  useEffect(() => {
    if (isEditMode) return
    const draftId = messageIdInThread ? messageIdInThread : roomId
    if (!_isEmpty(draftData?.[draftId]?.files)) {
      const files: IFiles = []
      const filesInDraftData = draftData?.[draftId]?.files
      const combinedFiles = [...replicateInputData.current.files, ...filesInDraftData]
      combinedFiles.forEach((file) => {
        const existingFileIndex = files.findIndex(
          (localFile) => localFile.uniqueId === file.uniqueId,
        )
        if (existingFileIndex === -1) {
          files.push(file)
        } else if (file.url) {
          files[existingFileIndex] = file
        }
      })
      setInputData((prev) => ({ ...prev, files }))
    }
  }, [draftData?.[messageIdInThread || roomId]?.files])

  useEffect(() => {
    if (isEditMode) return
    const draftId = messageIdInThread || roomId
    if (draftId && !initialInputData) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...rest } = draftData?.[draftId] || {}
      setInputData({ ...initialState, ...rest })
      dispatch(conversationActions.removeDataInDraft({ id: draftId }))
      dispatch(uploadActions.removeFromQueueById({ id: draftId }))
    } else if (draftId && initialInputData) {
      setInputData({ ...initialState, ...initialInputData })
      dispatch(conversationActions.removeDataInDraft({ id: draftId }))
    }
  }, [messageIdInThread, roomId])

  const onCancelFile = (uniqueId: string) => {
    if (isEditMode) {
      const files = editMessageData.files.filter((file: ICustomFile) => uniqueId !== file.uniqueId)
      setEditMessageData((prev) => ({ ...prev, files }))
    } else {
      const files = inputData.files.filter((file: ICustomFile) => uniqueId !== file.uniqueId)
      setInputData((prev) => ({ ...prev, files }))
      dispatch(
        conversationActions.removeDataByUniqueIdInDraft({
          uniqueId,
          id: messageIdInThread || roomId,
        }),
      )
    }
    //uploadApi.cancelUploadFile()//Tüm upload state'lerini kesiyor.
    dispatch(uploadActions.removeFromQueue({ uniqueId }))
  }

  const onChangeInputData = async (text: string) => {
    const mentionedUsers = getParseMentionedUsers(text)
    if (isEditMode) {
      setEditMessageData((prev) => ({ ...prev, text, value: text, mentionedUsers }))
    } else {
      setInputData((prev) => ({ ...prev, text, value: text, mentionedUsers }))
    }
  }

  const onSelectEmoji = (emojiParams: EmojiClickData) => {
    if (isEditMode) {
      const text = editMessageData.text + emojiParams.emoji
      const value = editMessageData.value + emojiParams.emoji
      setEditMessageData((prev) => ({ ...prev, text, value }))
    } else {
      const text = inputData.text + emojiParams.emoji
      const value = inputData.value + emojiParams.emoji
      setInputData((prev) => ({ ...prev, text, value }))
    }
  }

  const onSendMessage = async () => {
    const sentAt = new Date().toISOString()

    const mentionedUserData = parseUserDataFromInputData(isEditMode ? editMessageData : inputData)
    users = { ...mentionedUserData, ...users }

    const {
      mentionedUsers,
      text,
      files = [],
    } = getInputValueByfullname(isEditMode ? editMessageData : inputData)
    const value = geValueByfullname(text, mentionedUsers, users)
    const trimmedText = text.trim()
    const cti = v4()
    const params: {
      type: Array<string>
      sentAt: string
      content: {
        mentionedUsers: Array<string>
        text: string
        value: string
        files?: IFiles
      }
      userData?: IUserData
      users?: IUser[]
    } = {
      type: [],
      sentAt,
      content: { mentionedUsers, text: trimmedText, value, files },
      userData: users,
      users: values(users),
    }

    if (!isEmpty(text)) {
      params.type.push('text')
    }
    const paramsClone: any = cloneDeep(params)

    delete paramsClone.users
    delete paramsClone.userData

    if (isEditMode) {
      setEditMessageData({ ...initialState })
    } else {
      setInputData({ ...initialState })
      dispatch(conversationActions.removeDataInDraft({ id: messageIdInThread || roomId }))
    }

    if (!_isEmpty(files)) {
      paramsClone.type.push('files')

      roomId ? (paramsClone.roomId = roomId) : null
      messageIdInThread ? (paramsClone.messageId = messageIdInThread) : messageId
      // Tek bir satırda method'u belirle, asla undefined olmasın
      paramsClone.method = messageIdInThread
        ? 'createThread'
        : isOtr
        ? 'createOtr'
        : messageId
        ? 'createMessage'
        : 'createMessage' // fallback default

      dispatch(uploadActions.startQueue({ ...paramsClone, cti }))

      if (messageIdInThread) {
        pendingHandler?.({
          ...paramsClone,
          roomId,
          sender: myId,
          status: MessageStatus.PENDING,
          cti,
          userData: users,
        })
      } else if (isOtr) {
        dispatch(
          otrActions.sendOtrMessage({
            ...paramsClone,
            roomId,
            status: MessageStatus.PENDING,
            sender: myId,
            offlineUsers: [],
            cti,
          }),
        )
      } else {
        dispatch(
          conversationActions.sendMessage({
            ...paramsClone,
            roomId,
            sender: myId,
            status: MessageStatus.PENDING,
            cti,
          }),
        )
      }
      return
    }

    try {
      if (messageIdInThread) {
        const threadMessage: any = await sendThreadMessage({
          messageId: messageIdInThread,
          cti,
          ...paramsClone,
        })
        if (threadMessage?.data?.success) {
          pendingHandler?.({
            ...threadMessage.data.result,
            roomId,
            sender: myId,
            status: MessageStatus.SENT,
            cti,
          })
        }
      } else if (messageId) {
        await sendEditedMessage({ messageId, cti, ...paramsClone })
      } else if (roomId) {
        if (isOtr) {
          const otrMessage: any = await sendOtrMessage({
            roomId,
            sender: myId,
            cti,
            ...paramsClone,
          })
          if (!otrMessage?.data?.success) {
            console.log('OTR mesajı gönderilirken bir hata oluştu')
          }
        } else {
          const message: any = await sendMessage({ roomId, cti, ...paramsClone })
          if (message?.data?.success) {
            dispatch(
              conversationActions.sendMessage({
                _id: message.data.data._id,
                roomId,
                sender: myId,
                status: MessageStatus.SENT,
                cti,
                ...message.data.data,
              }),
            )
          }
        }
      }
    } catch (e) {
      if (messageIdInThread) {
        pendingHandler?.({
          ...params,
          cti,
          roomId,
          sender: myId,
          status: MessageStatus.PENDING,
          userData: users,
        })
        await sendThreadMessage({ messageId: messageIdInThread, cti, ...paramsClone })
      } else if (messageId) {
        await sendEditedMessage({ messageId, cti, ...paramsClone })
      } else if (!isOtr) {
        await sendMessage({ roomId, cti, ...paramsClone })
      }
    }
  }

  const addDataInDraft = (
    beforeData: IMessageInputState,
    isRefresh: boolean,
    beforeMessageIdInThread?: string,
    beforeMessageRoomId?: string,
  ) => {
    if (isEditMode) return
    if (inputDataValidation(beforeData.text, beforeData.files)) {
      const filterFiles = isRefresh
        ? beforeData.files.filter((file) => file?.url)
        : beforeData.files
      if (beforeMessageRoomId && !beforeMessageIdInThread) {
        dispatch(
          conversationActions.addDataInDraft({
            ...beforeData,
            files: filterFiles,
            id: beforeMessageRoomId,
          }),
        )
      } else if (beforeMessageIdInThread) {
        dispatch(
          conversationActions.addDataInDraft({
            ...beforeData,
            files: filterFiles,
            id: beforeMessageIdInThread,
          }),
        )
      }
    }
  }

  return {
    inputData: isEditMode ? editMessageData : inputData,
    isValid,
    onCancelFile,
    onSendMessage,
    onSelectEmoji,
    onChangeInputData,
  }
}

export default useMessageInput
